import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  Briefcase,
  Building,
  Building2,
  CheckCircle,
  ChevronDown,
  Clock,
  DollarSign,
  FileText,
  Globe,
  Heart,
  Mail,
  Shield,
  Star,
  TrendingUp,
  User,
  UserCheck,
  UserPlus,
  Users,
  Zap
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  // Handle dropdown hover with delay
  const handleDropdownEnter = (dropdownName) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setActiveDropdown(dropdownName);
  };

  const handleDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150); // 150ms delay before closing
    setDropdownTimeout(timeout);
  };

  const handleJobSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.append('search', searchQuery.trim());
    if (searchLocation.trim()) params.append('location', searchLocation.trim());
    
    const queryString = params.toString();
    navigate(`/jobs${queryString ? `?${queryString}` : ''}`);
  };

  const handlePopularSearch = (term) => {
    const params = new URLSearchParams();
    params.append('search', term);
    navigate(`/jobs?${params.toString()}`);
  };

  const dropdownData = {
    signIn: [
      { name: 'As User', path: '/login', icon: User, description: 'Job seeker login' },
      { name: 'As Recruiter', path: 'https://recruiter.jobzentro.com/login', icon: UserCheck, description: 'Recruiter portal' },
      { name: 'As Employer', path: 'https://employer.jobzentro.com/login', icon: Building, description: 'HRMS dashboard' }
    ],
    getStarted: [
      { name: 'Join as User', path: '/register', icon: User, description: 'Start job hunting' },
      { name: 'Join as Recruiter', path: 'https://recruiter.jobzentro.com/register', icon: UserCheck, description: 'Start recruiting' },
      { name: 'Join as Employer', path: 'https://employer.jobzentro.com/register', icon: Building, description: 'Start managing' }
    ],
    pricing: [
      { name: 'Free Plan', path: '/pricing', icon: DollarSign, description: 'Basic features', price: 'Free' },
      { name: 'Pro Plan', path: '/pricing', icon: DollarSign, description: 'Advanced features', price: '$29/mo' },
      { name: 'Enterprise', path: '/pricing', icon: DollarSign, description: 'Custom solutions', price: 'Contact' }
    ],
    products: [
      { name: 'Time Clock', path: '/products/time-clock', icon: Clock, description: 'Employee time tracking' },
      { name: 'Invoices', path: '/products/invoices', icon: FileText, description: 'Automated invoicing' },
      { name: 'Time Sheets', path: '/products/timesheets', icon: Clock, description: 'Timesheet management' },
      { name: 'Job Matching', path: '/products/job-matching', icon: Users, description: 'AI-powered matching' }
    ]
  };

  const stats = [
    { icon: Users, label: 'Active Users', value: '50,000+', color: 'text-blue-600' },
    { icon: Briefcase, label: 'Job Openings', value: '10,000+', color: 'text-green-600' },
    { icon: Building2, label: 'Companies', value: '2,500+', color: 'text-purple-600' },
    { icon: Award, label: 'Success Stories', value: '15,000+', color: 'text-orange-600' }
  ];

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Matching',
      description: 'Advanced algorithms match the right talent with the right opportunities.',
      color: 'text-blue-600'
    },
    {
      icon: Shield,
      title: 'Verified Companies',
      description: 'All employers are thoroughly vetted for your safety and security.',
      color: 'text-green-600'
    },
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'Personalized insights and recommendations to advance your career.',
      color: 'text-purple-600'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock assistance for all your questions and concerns.',
      color: 'text-orange-600'
    }
  ];

  const hrmsFeatures = [
    {
      icon: Users,
      title: 'Employee Management',
      description: 'Create and manage multiple employees with comprehensive profiles.',
      color: 'text-blue-600'
    },
    {
      icon: Clock,
      title: 'Time Tracking',
      description: 'Automated time tracking and timesheet management for all employees.',
      color: 'text-green-600'
    },
    {
      icon: FileText,
      title: 'Invoice Generation',
      description: 'Generate and manage invoices automatically based on time tracking.',
      color: 'text-purple-600'
    },
    {
      icon: Briefcase,
      title: 'Job Posting',
      description: 'Post jobs, manage applications, and find the perfect candidates.',
      color: 'text-orange-600'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Software Engineer',
      company: 'TechCorp',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b1fa?w=100&h=100&fit=crop&crop=face',
      content: 'JobZentro helped me find my dream job in just 2 weeks. The AI matching was incredibly accurate!',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'HR Director',
      company: 'InnovateX',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      content: 'The HRMS features have transformed our operations. Employee management and time tracking are seamless.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'Recruiter',
      company: 'StartupHub',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      content: 'As a recruiter, JobZentro has streamlined our hiring process. We find top talent efficiently.',
      rating: 5
    }
  ];

  const companies = [
    { name: 'TechCorp', logo: 'https://via.placeholder.com/120x60/1e75ab/ffffff?text=TechCorp' },
    { name: 'InnovateX', logo: 'https://via.placeholder.com/120x60/a2bd38/ffffff?text=InnovateX' },
    { name: 'StartupHub', logo: 'https://via.placeholder.com/120x60/8b5cf6/ffffff?text=StartupHub' },
    { name: 'GrowthCo', logo: 'https://via.placeholder.com/120x60/f59e0b/ffffff?text=GrowthCo' },
    { name: 'FutureTech', logo: 'https://via.placeholder.com/120x60/ec4899/ffffff?text=FutureTech' },
    { name: 'DataLabs', logo: 'https://via.placeholder.com/120x60/1e75ab/ffffff?text=DataLabs' }
  ];

  const jobCategories = [
    { name: 'Technology', count: '3,240', icon: '💻', color: 'bg-blue-100 text-blue-600' },
    { name: 'Marketing', count: '1,850', icon: '📱', color: 'bg-green-100 text-green-600' },
    { name: 'Design', count: '950', icon: '🎨', color: 'bg-purple-100 text-purple-600' },
    { name: 'Sales', count: '2,100', icon: '💼', color: 'bg-orange-100 text-orange-600' },
    { name: 'Finance', count: '1,200', icon: '💰', color: 'bg-pink-100 text-pink-600' },
    { name: 'Healthcare', count: '800', icon: '🏥', color: 'bg-teal-100 text-teal-600' }
  ];

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmail('');
  };

  const DropdownMenu = ({ items, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
        onMouseEnter={() => {
          if (dropdownTimeout) {
            clearTimeout(dropdownTimeout);
            setDropdownTimeout(null);
          }
        }}
        onMouseLeave={handleDropdownLeave}
      >
        {items.map((item, index) => {
          const Icon = item.icon;
          const isExternal = item.path.startsWith('http');
          
          return (
            <div key={index}>
              {isExternal ? (
                <a
                  href={item.path}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-200 group"
                  onClick={onClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                        {item.name}
                      </span>
                      {item.price && (
                        <span className="text-sm font-semibold text-blue-600">{item.price}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </a>
              ) : (
                <Link
                  to={item.path}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-200 group"
                  onClick={onClose}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                        {item.name}
                      </span>
                      {item.price && (
                        <span className="text-sm font-semibold text-blue-600">{item.price}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </Link>
              )}
            </div>
          );
        })}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 backdrop-blur-xl border-b border-white/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <motion.div 
              className="flex items-center space-x-3 group cursor-pointer"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-lg blur-md group-hover:blur-lg transition-all duration-300"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-lg p-1.5 border border-white/20">
                  <img 
                    src="/logo1.svg" 
                    alt="JobZentro Logo" 
                    className="w-6 h-6"
                  />
                </div>
              </div>
              <div className="relative">
                <h1 className="text-lg font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-yellow-300 group-hover:to-orange-300 transition-all duration-300">
                  JobZentro
                </h1>
                <p className="text-xs text-white/70 font-medium group-hover:text-white/90 transition-colors duration-300">
                  AI HRMS Platform
                </p>
              </div>
            </motion.div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-2">
              {[
                
                { name: 'Jobs', path: '/jobs', icon: '💼' },
                { name: 'About', path: '/about', icon: 'ℹ️' },
                { name: 'Contact', path: '/contact', icon: '📞' }
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="relative"
                >
                  <Link 
                    to={item.path}
                    className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-white font-medium hover:bg-white/10 hover:font-semibold transition-all duration-200"
                  >
                    <span className="text-sm hover:scale-110 transition-transform duration-200">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                </motion.div>
              ))}

              {/* Products Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="relative dropdown-container"
                onMouseEnter={() => handleDropdownEnter('products')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'products' ? null : 'products')}
                  className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-white font-medium hover:bg-white/10 hover:font-semibold transition-all duration-200 cursor-pointer"
                >
                  <span className="text-sm">📦</span>
                  <span>Products</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'products' ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu
                  items={dropdownData.products}
                  isOpen={activeDropdown === 'products'}
                  onClose={() => setActiveDropdown(null)}
                />
              </motion.div>

              {/* Pricing Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                className="relative dropdown-container"
                onMouseEnter={() => handleDropdownEnter('pricing')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'pricing' ? null : 'pricing')}
                  className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-white font-medium hover:bg-white/10 hover:font-semibold transition-all duration-200 cursor-pointer"
                >
                  <span className="text-sm">💰</span>
                  <span>Pricing</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'pricing' ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu
                  items={dropdownData.pricing}
                  isOpen={activeDropdown === 'pricing'}
                  onClose={() => setActiveDropdown(null)}
                />
              </motion.div>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-3">
              {/* Sign In Button with Dropdown */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.6 }}
                className="relative dropdown-container"
                onMouseEnter={() => handleDropdownEnter('signIn')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'signIn' ? null : 'signIn')}
                  className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-white font-medium hover:bg-white/10 hover:font-semibold transition-all duration-200 cursor-pointer"
                >
                  <span className="text-sm hover:rotate-12 transition-transform duration-200">🔑</span>
                  <span>Sign in</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'signIn' ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu
                  items={dropdownData.signIn}
                  isOpen={activeDropdown === 'signIn'}
                  onClose={() => setActiveDropdown(null)}
                />
              </motion.div>

              {/* Get Started Button with Dropdown */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.7 }}
                className="relative dropdown-container"
                onMouseEnter={() => handleDropdownEnter('getStarted')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'getStarted' ? null : 'getStarted')}
                  className="relative flex items-center space-x-1.5 px-6 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
                >
                  <span className="text-sm hover:scale-110 hover:rotate-12 transition-all duration-200">🚀</span>
                  <span className="hover:font-bold transition-all duration-200">Get started</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'getStarted' ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu
                  items={dropdownData.getStarted}
                  isOpen={activeDropdown === 'getStarted'}
                  onClose={() => setActiveDropdown(null)}
                />
              </motion.div>

              {/* Mobile Menu Button */}
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.8 }}
                className="md:hidden relative group p-1.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 cursor-pointer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // TODO: Implement mobile menu toggle
                  console.log('Mobile menu clicked');
                }}
              >
                <div className="w-5 h-5 flex flex-col justify-center items-center space-y-0.5">
                  <div className="w-4 h-0.5 bg-white rounded-full group-hover:bg-yellow-300 transition-all duration-300 transform group-hover:rotate-45 group-hover:translate-y-1"></div>
                  <div className="w-4 h-0.5 bg-white rounded-full group-hover:bg-orange-300 transition-all duration-300 opacity-100 group-hover:opacity-0"></div>
                  <div className="w-4 h-0.5 bg-white rounded-full group-hover:bg-red-300 transition-all duration-300 transform group-hover:-rotate-45 group-hover:-translate-y-1"></div>
                </div>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1 left-1/4 w-1 h-1 bg-yellow-300 rounded-full animate-ping opacity-60"></div>
          <div className="absolute top-2 right-1/3 w-0.5 h-0.5 bg-pink-300 rounded-full animate-ping animation-delay-2000 opacity-60"></div>
          <div className="absolute bottom-1 left-1/2 w-1 h-1 bg-cyan-300 rounded-full animate-ping animation-delay-4000 opacity-60"></div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden flex items-center">
        {/* Background Design Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-100/30 via-transparent to-purple-100/30"></div>
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-20 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/4 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="px-4 sm:px-6 lg:px-8 lg:pl-12 xl:pl-20"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-8">
                <Star className="w-4 h-4 mr-2" />
                Trusted by 50,000+ professionals
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
                SCHEDULE. MANAGE. HIRE.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">ALL FROM ONE EASY APP.</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-2xl">
                Put job searching, employee management, time tracking, and HR all in one place with JobZentro.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 mb-16">
                <Link to="/register" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-lg shadow-xl hover:shadow-2xl transform hover:scale-105">
                  Get started for free
                </Link>
                <Link to="/jobs" className="bg-white text-gray-900 px-10 py-5 rounded-2xl font-semibold border-2 border-gray-200 hover:bg-gray-50 transition-all duration-300 text-lg shadow-lg hover:shadow-xl transform hover:scale-105">
                  Browse jobs
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">50K+</div>
                  <div className="text-sm text-gray-600 font-medium">Active Users</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">10K+</div>
                  <div className="text-sm text-gray-600 font-medium">Job Openings</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">95%</div>
                  <div className="text-sm text-gray-600 font-medium">Success Rate</div>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Lottie Animation */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 lg:pr-12 xl:pr-20"
            >
              <div className="relative w-full max-w-2xl">
                {/* Lottie Animation Container */}
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/20">
                  <div className="w-full h-96 flex items-center justify-center">
                    {/* Lottie Animation - You can replace this with actual Lottie */}
                    <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center relative overflow-hidden">
                      {/* Animated Illustration */}
                      <div className="absolute inset-0">
                        <div className="absolute top-8 left-8 w-16 h-16 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="absolute top-16 right-12 w-12 h-12 bg-purple-400 rounded-full animate-bounce animation-delay-2000"></div>
                        <div className="absolute bottom-16 left-12 w-14 h-14 bg-indigo-400 rounded-full animate-bounce animation-delay-4000"></div>
                        <div className="absolute bottom-8 right-8 w-10 h-10 bg-pink-400 rounded-full animate-bounce animation-delay-1000"></div>
                      </div>
                      
                      {/* Main Illustration */}
                      <div className="relative z-10 text-center">
                        <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                          <Briefcase className="w-16 h-16 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">AI-Powered Job Matching</h3>
                        <p className="text-gray-600 max-w-xs mx-auto">
                          Find your perfect job match with our intelligent algorithm
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-yellow-400 rounded-full animate-float"></div>
                <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-green-400 rounded-full animate-float animation-delay-2000"></div>
                <div className="absolute top-1/2 -right-8 w-4 h-4 bg-red-400 rounded-full animate-float animation-delay-4000"></div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Quick Job Search - Floating at bottom */}
        {/* <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-full max-w-2xl px-4"
        >
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Quick Job Search</h3>
            <form onSubmit={handleJobSearch} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Job title, company, or keywords..."
                    className="w-full pl-10 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Location"
                    className="w-full pl-10 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                  />
                </div>
              </div>
              <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                Search jobs
              </button>
            </form>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3 text-center">Popular searches:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['Software Engineer', 'Product Manager', 'Data Scientist', 'UX Designer'].map((term) => (
                  <button
                    key={term}
                    onClick={() => handlePopularSearch(term)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div> */}
      </section>

      {/* User Types Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Design Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
              <Star className="w-4 h-4 mr-2" />
              Choose Your Path
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Perfect for everyone
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're seeking opportunities, hiring talent, or managing a workforce, we have the perfect solution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {/* Job Seekers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full">
                <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Job Seekers</h3>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  Find your dream job with AI-powered matching and personalized recommendations.
                </p>
                <ul className="text-left space-y-4 mb-10">
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">AI-powered job matching</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Application tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Career insights</span>
                  </li>
                </ul>
                <Link to="/register" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 inline-block text-center">
                  Start job hunting
                </Link>
              </div>
            </motion.div>

            {/* Recruiters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-700 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full">
                <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <UserPlus className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Recruiters</h3>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  Find and hire top talent with our AI-powered job matching and candidate screening.
                </p>
                <ul className="text-left space-y-4 mb-10">
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Post job openings</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Candidate screening</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Application management</span>
                  </li>
                </ul>
                <Link to="/recruiter/register" className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 inline-block text-center">
                  Start recruiting
                </Link>
              </div>
            </motion.div>

            {/* Employers/HRMS */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full">
                <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Employers</h3>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  Complete HRMS solution for employee management and operations.
                </p>
                <ul className="text-left space-y-4 mb-10">
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Employee management</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Time tracking</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Invoice generation</span>
                  </li>
                </ul>
                <Link to="/employer/register" className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 inline-block text-center">
                  Start managing
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Companies */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-gray-600 font-medium mb-8">Trusted by leading companies worldwide</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
              {companies.map((company, index) => (
                <motion.div
                  key={company.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300"
                >
                  <img src={company.logo} alt={company.name} className="h-12 object-contain" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The everything app for hourly teams.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of professionals who have transformed their careers with JobZentro's innovative platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gray-100 flex items-center justify-center`}>
                    <Icon className={`w-8 h-8 ${stat.color}`} />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why choose JobZentro?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of job searching and HR management with our advanced features.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="flex items-start space-x-6 p-6 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HRMS Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Complete HRMS Solution
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Manage your entire workforce with our comprehensive HR management system.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {hrmsFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="flex items-start space-x-6 p-6 rounded-xl hover:bg-white transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

    
      {/* Job Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Explore job categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover opportunities across diverse industries and find the perfect role.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.append('search', category.name);
                  navigate(`/jobs?${params.toString()}`);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center text-xl`}>
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                      <p className="text-gray-600 text-sm">{category.count} jobs available</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What our users are saying
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Hear from professionals who have transformed their careers.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-gray-50 p-8 rounded-2xl"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role} at {testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to transform your career?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of professionals who have found their dream jobs through JobZentro.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-colors text-lg">
                Get started free
              </Link>
              <Link to="/jobs" className="bg-transparent text-white px-8 py-4 rounded-lg font-medium border border-white hover:bg-white hover:text-blue-600 transition-colors text-lg">
                Browse jobs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">JobZentro</h3>
                  <p className="text-sm text-gray-400">AI HRMS Platform</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Connecting exceptional talent with outstanding opportunities through innovative technology.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-6">For Job Seekers</h4>
              <ul className="space-y-3">
                <li><Link to="/jobs" className="text-gray-400 hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/register" className="text-gray-400 hover:text-white transition-colors">Create Profile</Link></li>
                <li><Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">Dashboard</Link></li>
                <li><Link to="/career-advice" className="text-gray-400 hover:text-white transition-colors">Career Advice</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-6">For Employers</h4>
              <ul className="space-y-3">
                <li><Link to="/employer/register" className="text-gray-400 hover:text-white transition-colors">Post Jobs</Link></li>
                <li><Link to="/employer/dashboard" className="text-gray-400 hover:text-white transition-colors">Employer Dashboard</Link></li>
                <li><Link to="/talent-search" className="text-gray-400 hover:text-white transition-colors">Search Talent</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-6">Company</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-center md:text-left">
              &copy; 2024 JobZentro. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Heart className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Globe className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 